// import jwtConfig from 'config/jwt.config';
import { env } from 'config/env';
import siteConfig from 'config/site.config';
import { forceRefreshToken, getAuthToken } from 'library/auth/authProvider';
import { store } from 'reduxStore/store';
import { authActions } from 'reduxStore/auth/slice';
import { Notification } from 'zComponents/atoms';
import {
	billingItemRefusals,
	billingPartialMessage,
	billingRefusalFromEnvelope,
	billingRefusalMessage
} from 'library/helpers/billingRefusal';

export const getOrgID = () => {
	var state = store.getState() || undefined;

	if (state.Auth?.selectedProfile?.orgID) {
		return state.Auth.selectedProfile.orgID || undefined;
	} else {
		return undefined; //? Sends undefined to the backend so the request is thrown and the error is handled.
	}
};

/**
 * The `/v5` root for THIS environment, without an org segment.
 *
 * Almost everything the dashboard calls is org-scoped, which is why SuperFetch pins an orgID into every
 * URL. A few boundaries are deliberately ORG-LESS because the thing they answer is a property of the
 * USER, not of an org — `/v5/partner-portal/*` is one (partner identity spans every org). Those callers
 * need the same environment resolution without the org, and this exists so they get it from here rather
 * than re-deriving the domain and drifting from it.
 */
export function applicationServerV5Root() {
	const state = store.getState();
	const environment = state?.App?.environment || 'production';

	if (process.env.NODE_ENV === 'development') {
		return `http://${window.location.hostname}:${env.localApiPort}/v5`;
	}
	return `https://${siteConfig.getApplicationServerBaseDomain(environment)}/v5`;
}

export function applicationServerURLGenerator(orgID) {
	return `${applicationServerV5Root()}/${orgID || getOrgID()}`;
}

// Provider-aware bearer token. authProvider returns a Kinde access token when the
// Kinde flag is on and a Kinde session is live, otherwise the unchanged Firebase
// id-token path. The error UX (toast + undefined → no Authorization header) is
// preserved from the original Firebase-only implementation.
const getToken = async () => {
	try {
		return await getAuthToken();
	} catch (e) {
		Notification('error', 'There is an Error! Please reload the page.');
		console.log(e);
		return undefined;
	}
};

export const CLIENT_HEADERS = Object.freeze({
	'X-Zezamii-Client': 'dashboard',
	'X-Zezamii-Device-Platform': 'web'
});

const customHeader = async () => {
	const token = await getToken();
	return {
		'Content-Type': 'application/json',
		'Accept': 'application/json',
		// Names this client in the App Server's request log and Log.UserActivityDaily (constants/clients.js
		// there). Without it every Dashboard call read as 'unknown'.
		...CLIENT_HEADERS,
		...(token ? { Authorization: `Bearer ${token}` } : {})
	};
};

// A 401 is the only signal that a provider session has ended, and every App Server call passes
// through here — so this is the one place that can notice. SessionWatcher covers the no-traffic case.
let inFlightRecovery = null; // one renewal shared by a whole burst of concurrent 401s
let expiryDispatched = false; // latch: at most one sign-out per dead session

/**
 * Answer a 401: renew the token, or end the session when there is nothing left to renew.
 * @returns {Promise<boolean>} true → retry once; false → session over, or there never was one.
 */
const renewOrEndSession = async () => {
	// Public pages call this module anonymously, so a 401 there is the normal answer, not an expiry.
	// Signed out also releases the latch, so the next session in this tab gets its own verdict.
	if (!store.getState()?.Auth?.isLoggedIn) {
		expiryDispatched = false;
		return false;
	}

	if (expiryDispatched) return false; // already on the way out; in-flight stragglers land here

	// One renewal for the whole burst — N concurrent exchanges would race on the same rotating
	// refresh token. Identity guard on cleanup so a late attempt cannot clear a newer one.
	if (!inFlightRecovery) {
		const thisRecovery = (async () => {
			await forceRefreshToken();
			// 🛑 The verdict is "can I still get a token?", never "did the retry 401 again?" —
			// /partner-admin's staff gate 401s a valid session and would expel a working admin.
			return Boolean(await getAuthToken());
		})()
			.catch(() => false)
			.then((alive) => {
				if (inFlightRecovery === thisRecovery) inFlightRecovery = null;
				return alive;
			});
		inFlightRecovery = thisRecovery;
	}

	if (await inFlightRecovery) return true;

	// 🛑 Re-checked AFTER the await: the whole burst clears the early check together, so without
	// this four concurrent requests ordered four sign-outs (what the burst test caught).
	if (expiryDispatched) return false;

	expiryDispatched = true;
	store.dispatch(authActions.sessionExpired());
	return false;
};

/**
 * Send a request, retrying once on a recoverable 401. A second 401 is returned unchanged.
 * @param {() => Promise<Response>} doFetch re-invoked, not replayed, so the retry rebuilds headers
 */
const fetchWithSessionRecovery = async (doFetch) => {
	const response = await doFetch();
	if (response.status !== 401) return response;
	return (await renewOrEndSession()) ? doFetch() : response;
};

/** Test-only: clear the one-renewal-per-burst latch between cases. Not used by app code. */
export const __resetSessionRecoveryForTests = () => {
	inFlightRecovery = null;
	expiryDispatched = false;
};

/**
 * Serialise query params, expanding an ARRAY value into REPEATED pairs:
 * `{ defaultCategoryID: [14, 19] }` → `defaultCategoryID=14&defaultCategoryID=19`.
 *
 * 🛑 This is the form the v5 routes parse. Joining an array with a comma (what the old inline
 * `key + '=' + encodeURIComponent(value)` produced) sends `defaultCategoryID=14%2C19`, which Joi
 * reads as one non-numeric value and rejects. An empty array contributes nothing at all.
 */
export const buildQueryString = (queryParam = {}) =>
	Object.entries(queryParam)
		.flatMap(([key, value]) =>
			Array.isArray(value)
				? value.map((entry) => `${key}=${encodeURIComponent(entry)}`)
				: [`${key}=${encodeURIComponent(value)}`]
		)
		.join('&');

// JS Fetch does not allow Query Params with the PATCH req. Please make sure that the query params are null when using the patch method.
const base = async (method, slug, body = {}, queryParam = {}, overrideOrgID) => {
	if (method === 'patch' && Object.keys(queryParam).length !== 0) {
		return { errors: 'Query parameters are not allowed with patch request' };
	} else {
		try {
			const url = `${applicationServerURLGenerator(overrideOrgID)}${slug}?${buildQueryString(queryParam)}`;
			const response = await fetchWithSessionRecovery(async () =>
				fetch(url, {
					method: method.toUpperCase(),
					headers: await customHeader(),
					body: method !== 'get' ? JSON.stringify(body) : null,
					cache: 'no-store'
				})
			);
			if (response.status === 204 || response.status === 304) {
				return { statusCode: response.status };
			}
			const res = await response.json();
			return withBillingRefusals(res, response.status, method);
		} catch (error) {
			return { errors: 'Server Error', error };
		}
	}
};

/**
 * Site-trial billing refusals (B18 402s and per-item refusals in 200s), handled once here for every org-scoped call.
 *
 * - A 402 with a billing gate code: nothing was applied. The result gains `billingRefusal`, and its `message` / `error`
 *   become the customer copy (what is paused, that doors keep working, who can act), so every saga's existing error
 *   path shows the right words without knowing about billing. The server's sentence stays in `serverMessage`.
 * - A 2xx write whose rows name billing refusals: the rest WAS applied, so the saga reports success. The result gains
 *   `billingRefusals[]` and one warning says which changes were not made and why.
 *
 * Routed on `errorCode` only (library/helpers/billingRefusal). Anything else passes through untouched.
 */
export const withBillingRefusals = (res, status, method) => {
	if (!res || typeof res !== 'object') return res;
	const refusal = billingRefusalFromEnvelope(status, res);
	if (refusal) {
		const copy = billingRefusalMessage(refusal);
		return {
			...res,
			billingRefusal: refusal,
			serverMessage: res.message ?? null,
			message: `${copy.title}. ${copy.message}`,
			error: `${copy.title}. ${copy.message}`
		};
	}
	if (status >= 200 && status < 300 && method !== 'get') {
		const items = billingItemRefusals(res);
		if (items.length) {
			const partial = billingPartialMessage(items);
			Notification('warning', partial.title, partial.message);
			return { ...res, billingRefusals: items };
		}
	}
	return res;
};

/**
 * POST raw bytes to an org-scoped route — the file IS the body, not a base64 field inside JSON.
 * Same base URL, token and answer shape as the methods above; only the serialisation differs.
 *
 * @param {string} slug - org-scoped path, e.g. `/media/person/7`
 * @param {File|Blob} file - the bytes; its type is sent as Content-Type but the server re-reads it
 * @param {Object} [queryParam]
 * @returns {Promise<Object>} the parsed response, or `{ errors: 'Server Error' }` on a network failure
 */
export const postBinary = async (slug, file, queryParam = {}) => {
	try {
		const query = buildQueryString(queryParam);
		const url = `${applicationServerURLGenerator()}${slug}?${query}`;

		// The token is fetched INSIDE the attempt, not once above it, so the retry carries the
		// renewed one. Same reason the JSON paths rebuild their headers per attempt.
		const response = await fetchWithSessionRecovery(async () => {
			const token = await getToken();
			return fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': file?.type || 'application/octet-stream',
					'Accept': 'application/json',
					...(token ? { Authorization: `Bearer ${token}` } : {})
				},
				body: file,
				cache: 'no-store'
			});
		});

		if (response.status === 204 || response.status === 304) {
			return { statusCode: response.status };
		}
		return await response.json();
	} catch (error) {
		return { errors: 'Server Error', error };
	}
};

/**
 * Read the download filename the server chose out of `Content-Disposition`.
 *
 * ⚠️ A cross-origin response only lets the browser read headers the server names in
 * `Access-Control-Expose-Headers`. The dashboard and the App Server are different origins in
 * every environment, so this returns `null` whenever that header is not exposed — the caller
 * MUST have its own filename ready rather than shipping a file called "download".
 *
 * @param {string|null} disposition the raw header value
 * @returns {string|null} the filename, or null when it cannot be read
 */
export const filenameFromDisposition = (disposition) => {
	if (!disposition) return null;
	// RFC 5987 (`filename*=UTF-8''…`) wins over the plain form when both are present.
	const encoded = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(disposition);
	if (encoded) {
		try {
			return decodeURIComponent(encoded[1].trim());
		} catch {
			/* fall through to the plain form */
		}
	}
	const plain = /filename="?([^";]+)"?/.exec(disposition);
	return plain ? plain[1].trim() : null;
};

/**
 * POST JSON to an org-scoped route whose SUCCESS answer is a FILE, not the v5 envelope.
 *
 * `/reports/download` generates synchronously and streams the bytes back; only its failures
 * come back as `{ statusCode, error }`. Branching on the status code (not on Content-Type) is
 * the simpler test and the one the reports contract recommends — so a 2xx is read as a blob and
 * anything else is parsed as the envelope every other SuperFetch caller already knows how to read.
 *
 * @param {string} slug org-scoped path, e.g. `/reports/download`
 * @param {Object} [body] JSON request body
 * @param {Object} [queryParam]
 * @returns {Promise<{statusCode:number, blob?:Blob, filename?:string|null}>} envelope-shaped either way
 */
export const postForBlob = async (slug, body = {}, queryParam = {}) => {
	try {
		const query = buildQueryString(queryParam);
		const url = `${applicationServerURLGenerator()}${slug}?${query}`;

		// Token fetched INSIDE the attempt so a 401 retry carries the renewed one — same reason
		// postBinary rebuilds its headers per attempt.
		const response = await fetchWithSessionRecovery(async () => {
			const token = await getToken();
			return fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					// No `Accept: application/json` — asking for JSON from a route that answers with
					// a spreadsheet is how a well-behaved server is talked into a 406.
					...(token ? { Authorization: `Bearer ${token}` } : {})
				},
				body: JSON.stringify(body),
				cache: 'no-store'
			});
		});

		if (!response.ok) {
			// The failure IS the envelope. A body that isn't JSON (a proxy's HTML 502) must not
			// throw here, or the caller sees "Server Error" instead of the status it really got.
			try {
				const parsed = await response.json();
				return { statusCode: response.status, ...parsed };
			} catch {
				return { statusCode: response.status };
			}
		}

		return {
			statusCode: response.status,
			blob: await response.blob(),
			filename: filenameFromDisposition(response.headers.get('content-disposition'))
		};
	} catch (error) {
		return { errors: 'Server Error', error };
	}
};

/**
 * Base fetch for routes that don't require an orgID in the URL.
 * Uses the server base URL with /v5 prefix only (no /:orgID).
 */
const baseWithoutOrg = async (method, slug, body = {}, queryParam = {}) => {
	try {
		let baseUrl;
		if (process.env.NODE_ENV === 'development') {
			baseUrl = `http://${window.location.hostname}:${env.localApiPort}/v5`;
		} else {
			const state = store.getState();
			const environment = state?.App?.environment || 'production';
			const domain = siteConfig.getApplicationServerBaseDomain(environment);
			baseUrl = `https://${domain}/v5`;
		}

		const url = `${baseUrl}${slug}?${Object.keys(queryParam)
			.map((key) => key + '=' + encodeURIComponent(queryParam[key]))
			.join('&')}`;
		const response = await fetchWithSessionRecovery(async () =>
			fetch(url, {
				method: method.toUpperCase(),
				headers: await customHeader(),
				body: method !== 'get' ? JSON.stringify(body) : null,
				cache: 'no-store'
			})
		);
		if (response.status === 204 || response.status === 304) {
			return { statusCode: response.status };
		}
		return await response.json();
	} catch (error) {
		return { errors: 'Server Error', error };
	}
};

const SuperFetch = {};
['get', 'post', 'put', 'delete', 'patch'].forEach((method) => {
	SuperFetch[method] = base.bind(null, method);
});

// Org-less family (no /:orgID) — for global/staff boundaries mounted before /:orgID (e.g. /partner-admin).
['get', 'post', 'put', 'delete', 'patch'].forEach((method) => {
	SuperFetch[`${method}WithoutOrg`] = baseWithoutOrg.bind(null, method);
});

export default SuperFetch;
